import React, { useEffect, useMemo, useState } from 'react'
import {
	ArrowLeft,
	ImageOff,
	Loader as Loader2,
	Package,
	Search,
} from 'lucide-react'
import { useInventory } from '../../contexts/InventoryContext'
import { useProductAttributes } from '../../hooks/use-product-attributes'
import {
	buildProductNameGroups,
	filterProductGroups,
	type ProductColorGroup,
	type ProductNameGroup,
} from '../../utils/productGrouping'
import { ProductGroupCard } from '../inhouse/product-group-card'
import { Button } from '../ui/Button'
import { HapticFeedback } from '../ui/HapticFeedback'
import { QuantityStepper } from '../ui/QuantityStepper'
import {
	MODEL_RENDER_CAP,
	colorHasRealSizes,
	displayColorName,
	displaySizeName,
	resolveColorOnlyProduct,
	resolveExtraProduct,
	shouldSkipColorStep,
	shouldSkipSizeStep,
	sortedSizesForGroup,
	type ExtraPickerProduct,
} from './extra-item-picker-helpers'

type DrillStep = 'models' | 'colors' | 'sizes' | 'qty'

interface ExtraItemPickerProps {
	adminId: string | null
	/** Qty already in the extras tally, keyed by product id. */
	addedQtyByProductId: Record<string, number>
	onAdd: (product: ExtraPickerProduct, qty: number) => void
}

export function ExtraItemPicker({
	adminId,
	addedQtyByProductId,
	onAdd,
}: ExtraItemPickerProps) {
	const { allItems, fetchAllItems, allItemsLoading } = useInventory()
	const { colors, sizes, isLoading: attributesLoading } = useProductAttributes(adminId)

	const [search, setSearch] = useState('')
	const [step, setStep] = useState<DrillStep>('models')
	const [selectedGroup, setSelectedGroup] = useState<ProductNameGroup | null>(null)
	const [selectedColor, setSelectedColor] = useState<ProductColorGroup | null>(null)
	const [pendingProduct, setPendingProduct] = useState<ExtraPickerProduct | null>(null)
	const [qty, setQty] = useState(1)
	const [loadError, setLoadError] = useState<string | null>(null)

	useEffect(() => {
		if (!adminId) return
		let cancelled = false
		setLoadError(null)
		void fetchAllItems().catch((err: unknown) => {
			if (cancelled) return
			setLoadError(err instanceof Error ? err.message : 'Failed to load inventory')
		})
		return () => {
			cancelled = true
		}
	}, [adminId, fetchAllItems])

	const quantityById = useMemo(() => {
		const map = new Map<string, number>()
		for (const item of allItems) map.set(item.id, item.quantity ?? 0)
		return map
	}, [allItems])

	const productGroups = useMemo(
		() =>
			buildProductNameGroups(
				allItems,
				colors,
				sizes,
				(productId) => quantityById.get(productId) ?? 0
			),
		[allItems, colors, sizes, quantityById]
	)

	const filteredGroups = useMemo(
		() => filterProductGroups(productGroups, search, colors),
		[productGroups, search, colors]
	)

	const visibleGroups = filteredGroups.slice(0, MODEL_RENDER_CAP)
	const isTruncated = filteredGroups.length > MODEL_RENDER_CAP

	const sizeColumns = useMemo(() => {
		if (!selectedGroup) return []
		return sortedSizesForGroup(selectedGroup, sizes)
	}, [selectedGroup, sizes])

	function resetToModels() {
		setStep('models')
		setSelectedGroup(null)
		setSelectedColor(null)
		setPendingProduct(null)
		setQty(1)
	}

	function openQty(product: ExtraPickerProduct) {
		setPendingProduct(product)
		setQty(1)
		setStep('qty')
	}

	function handleSelectGroup(group: ProductNameGroup) {
		HapticFeedback.light()
		setSelectedGroup(group)
		setSelectedColor(null)
		setPendingProduct(null)
		setQty(1)

		if (shouldSkipColorStep(group)) {
			const onlyColor = group.colors[0]
			setSelectedColor(onlyColor)
			if (shouldSkipSizeStep(onlyColor)) {
				const product = resolveColorOnlyProduct(group, onlyColor)
				if (product) openQty(product)
				else setStep('colors')
				return
			}
			setStep('sizes')
			return
		}

		setStep('colors')
	}

	function handleSelectColor(color: ProductColorGroup) {
		if (!selectedGroup) return
		HapticFeedback.light()
		setSelectedColor(color)
		setPendingProduct(null)
		setQty(1)

		if (shouldSkipSizeStep(color)) {
			const product = resolveColorOnlyProduct(selectedGroup, color)
			if (product) openQty(product)
			else setStep('sizes')
			return
		}

		setStep('sizes')
	}

	function handleSelectSize(sizeName: string) {
		if (!selectedGroup || !selectedColor) return
		HapticFeedback.light()
		const product = resolveExtraProduct(selectedGroup, selectedColor, sizeName)
		if (!product) return
		openQty(product)
	}

	function handleBack() {
		HapticFeedback.light()
		if (step === 'qty') {
			if (selectedColor && colorHasRealSizes(selectedColor)) {
				setPendingProduct(null)
				setQty(1)
				setStep('sizes')
				return
			}
			if (selectedGroup && !shouldSkipColorStep(selectedGroup)) {
				setPendingProduct(null)
				setSelectedColor(null)
				setQty(1)
				setStep('colors')
				return
			}
			resetToModels()
			return
		}
		if (step === 'sizes') {
			if (selectedGroup && shouldSkipColorStep(selectedGroup)) {
				resetToModels()
				return
			}
			setSelectedColor(null)
			setStep('colors')
			return
		}
		if (step === 'colors') {
			resetToModels()
		}
	}

	function handleConfirmAdd() {
		if (!pendingProduct || qty < 1) return
		HapticFeedback.success()
		onAdd(pendingProduct, qty)
		resetToModels()
	}

	const isLoading = allItemsLoading || attributesLoading

	if (isLoading && allItems.length === 0) {
		return (
			<div className="flex flex-col items-center justify-center py-10 gap-3">
				<Loader2 className="h-8 w-8 animate-spin text-violet-500" />
				<p className="text-sm text-gray-500 dark:text-gray-400">Loading inventory…</p>
			</div>
		)
	}

	if (loadError) {
		return (
			<div className="text-center py-8 space-y-2">
				<p className="text-sm text-rose-600 dark:text-rose-400">{loadError}</p>
				<p className="text-xs text-gray-500 dark:text-gray-400">
					You can still add extras by scanning a barcode or UHF tag.
				</p>
			</div>
		)
	}

	if (!isLoading && productGroups.length === 0) {
		return (
			<p className="text-center text-sm text-gray-500 dark:text-gray-400 py-8">
				No products in inventory yet.
			</p>
		)
	}

	return (
		<div className="space-y-3">
			{step !== 'models' && (
				<div className="flex items-center gap-2">
					<button
						type="button"
						onClick={handleBack}
						className="inline-flex items-center gap-1.5 text-sm font-medium text-violet-600 dark:text-violet-400 hover:underline"
					>
						<ArrowLeft className="h-4 w-4" />
						Back
					</button>
					<p className="text-xs text-gray-500 dark:text-gray-400 truncate flex-1 text-right">
						{[
							selectedGroup?.productName,
							selectedColor
								? displayColorName(selectedColor.colorName) || 'No colour'
								: null,
							step === 'qty' && pendingProduct
								? pendingProduct.size || null
								: null,
						]
							.filter(Boolean)
							.join(' · ')}
					</p>
				</div>
			)}

			{step === 'models' && (
				<>
					<div className="relative">
						<Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
						<input
							type="text"
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							placeholder="Filter models by name, SKU, barcode or colour…"
							className="w-full pl-12 pr-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-violet-500 focus:border-transparent"
						/>
					</div>

					<div className="space-y-2">
						{visibleGroups.map((group) => (
							<ProductGroupCard
								key={group.productName}
								group={group}
								density="list"
								showPricing={false}
								onSelect={handleSelectGroup}
								colorSwatches={colors}
							/>
						))}
						{filteredGroups.length === 0 && (
							<p className="text-center text-sm text-gray-500 dark:text-gray-400 py-4">
								No models match &quot;{search}&quot;.
							</p>
						)}
						{isTruncated && (
							<p className="text-center text-xs text-amber-600 dark:text-amber-400 py-2">
								Showing {MODEL_RENDER_CAP} of {filteredGroups.length} models — refine your
								search to narrow the list.
							</p>
						)}
					</div>
				</>
			)}

			{step === 'colors' && selectedGroup && (
				<div className="space-y-2">
					<p className="text-sm font-medium text-gray-700 dark:text-gray-300">
						Choose a colour
					</p>
					{selectedGroup.colors.map((color) => {
						const addedOnColor = Object.values(color.sizeItemIds).reduce(
							(sum, productId) => sum + (addedQtyByProductId[productId] ?? 0),
							0
						)
						const hex = color.hexCode || '#ccc'
						const label = displayColorName(color.colorName) || 'No colour'
						return (
							<button
								key={color.colorId}
								type="button"
								onClick={() => handleSelectColor(color)}
								className="w-full p-3 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-violet-300 dark:hover:border-violet-700 flex items-center gap-3 text-left transition-colors bg-white dark:bg-gray-800"
							>
								<span
									className="w-8 h-8 rounded-full border border-gray-300 dark:border-gray-600 flex-shrink-0 shadow-sm"
									style={{ backgroundColor: hex }}
									aria-hidden
								/>
								<div className="flex-1 min-w-0">
									<p className="font-medium text-gray-900 dark:text-gray-100 truncate">
										{label}
									</p>
									<p className="text-xs text-gray-500 dark:text-gray-400">
										{colorHasRealSizes(color)
											? `${Object.keys(color.sizeItemIds).length} sizes`
											: 'No size variants'}
									</p>
								</div>
								{addedOnColor > 0 && (
									<span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300">
										{addedOnColor} added
									</span>
								)}
							</button>
						)
					})}
				</div>
			)}

			{step === 'sizes' && selectedGroup && selectedColor && (
				<div className="space-y-2">
					<p className="text-sm font-medium text-gray-700 dark:text-gray-300">
						Choose a size
					</p>
					<div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
						{sizeColumns
							.filter((sizeName) => Boolean(selectedColor.sizeItemIds[sizeName]))
							.map((sizeName) => {
								const productId = selectedColor.sizeItemIds[sizeName]
								const already = productId ? addedQtyByProductId[productId] ?? 0 : 0
								const label = displaySizeName(sizeName) || 'One size'
								return (
									<button
										key={sizeName}
										type="button"
										onClick={() => handleSelectSize(sizeName)}
										className="relative p-3 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-violet-300 dark:hover:border-violet-700 bg-white dark:bg-gray-800 text-center transition-colors"
									>
										<span className="block font-semibold text-gray-900 dark:text-gray-100">
											{label}
										</span>
										{already > 0 && (
											<span className="mt-1 inline-block text-[11px] font-semibold px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300">
												{already} added
											</span>
										)}
									</button>
								)
							})}
					</div>
				</div>
			)}

			{step === 'qty' && pendingProduct && (
				<div className="rounded-xl border border-violet-200 dark:border-violet-800 bg-violet-50 dark:bg-violet-900/20 p-4 space-y-4">
					<div className="flex items-center gap-3">
						<div className="w-14 h-14 rounded-lg flex-shrink-0 overflow-hidden border border-violet-200 dark:border-violet-800 bg-white dark:bg-gray-900 flex items-center justify-center">
							{pendingProduct.image_url ? (
								<img
									src={pendingProduct.image_url}
									alt={pendingProduct.name || 'Product'}
									crossOrigin="anonymous"
									className="w-full h-full object-contain"
								/>
							) : (
								<ImageOff className="h-5 w-5 text-gray-400" />
							)}
						</div>
						<div className="flex-1 min-w-0">
							<p className="font-semibold text-gray-900 dark:text-gray-100 truncate">
								{pendingProduct.name || pendingProduct.sku || 'Item'}
							</p>
							<p className="text-xs text-gray-500 dark:text-gray-400 truncate">
								{[pendingProduct.color, pendingProduct.size].filter(Boolean).join(' / ') ||
									'No variant'}
								{pendingProduct.sku ? ` · SKU ${pendingProduct.sku}` : ''}
							</p>
							{(addedQtyByProductId[pendingProduct.id] ?? 0) > 0 && (
								<p className="text-xs text-violet-600 dark:text-violet-300 mt-1">
									Already added: {addedQtyByProductId[pendingProduct.id]}
								</p>
							)}
						</div>
					</div>

					<div className="flex items-center justify-between gap-3">
						<span className="text-sm font-medium text-gray-700 dark:text-gray-300">
							How many extra?
						</span>
						<QuantityStepper value={qty} onChange={setQty} min={1} max={999} size="md" />
					</div>

					<Button
						onClick={handleConfirmAdd}
						className="w-full py-3 text-base bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700"
					>
						<Package className="h-4 w-4 mr-2" />
						Add {qty} extra
					</Button>
				</div>
			)}
		</div>
	)
}
